// Local-first memory for Maya. Persists user mood, archetype, and visit count
// in localStorage. Designed to be swappable for a Supabase-backed profile later
// without changing the call sites.

export type MayaProfile = {
  moods: string[]; // selected mood ids over time
  lastMoods: string[]; // moods from last session
  archetype: string | null;
  visits: number;
  lastSeenAt: number;
  viewedTours: string[]; // tour ids the user opened
  mayaPlusInterest: boolean;
  // Orbit-era additions
  lastOrbit: string | null; // last universe opened
  orbitVisits: Record<string, number>; // counts per orbit id
  preferredMood: string | null;
  lastWhisperShown: string | null;
  favoriteTours: string[]; // tour ids the user starred
};

const KEY = "mm:maya:profile:v1";

const empty = (): MayaProfile => ({
  moods: [],
  lastMoods: [],
  archetype: null,
  visits: 0,
  lastSeenAt: 0,
  viewedTours: [],
  mayaPlusInterest: false,
  lastOrbit: null,
  orbitVisits: {},
  preferredMood: null,
  lastWhisperShown: null,
  favoriteTours: [],
});

export function toggleFavoriteTour(tourId: string): boolean {
  const current = loadMayaProfile();
  const has = current.favoriteTours.includes(tourId);
  const favoriteTours = has
    ? current.favoriteTours.filter((id) => id !== tourId)
    : [tourId, ...current.favoriteTours].slice(0, 100);
  saveMayaProfile({ ...current, favoriteTours, lastSeenAt: Date.now() });
  return !has;
}

export function isFavoriteTour(tourId: string): boolean {
  return loadMayaProfile().favoriteTours.includes(tourId);
}

export function loadMayaProfile(): MayaProfile {
  if (typeof window === "undefined") return empty();
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return empty();
    return { ...empty(), ...JSON.parse(raw) } as MayaProfile;
  } catch {
    return empty();
  }
}

export function saveMayaProfile(p: MayaProfile) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    /* quota: ignore */
  }
}

export function updateMayaProfile(patch: Partial<MayaProfile>): MayaProfile {
  const current = loadMayaProfile();
  const next = { ...current, ...patch, lastSeenAt: Date.now() };
  saveMayaProfile(next);
  return next;
}

export function recordMoods(moodIds: string[]) {
  const current = loadMayaProfile();
  const moods = Array.from(new Set([...current.moods, ...moodIds])).slice(-20);
  saveMayaProfile({
    ...current,
    moods,
    lastMoods: moodIds,
    preferredMood: moodIds[0] ?? current.preferredMood,
    lastSeenAt: Date.now(),
  });
}

export function recordArchetype(archetype: string) {
  updateMayaProfile({ archetype });
}

export function recordTourView(tourId: string) {
  const current = loadMayaProfile();
  const viewedTours = Array.from(new Set([tourId, ...current.viewedTours])).slice(0, 30);
  saveMayaProfile({ ...current, viewedTours, lastSeenAt: Date.now() });
}

export function bumpVisit() {
  const current = loadMayaProfile();
  saveMayaProfile({ ...current, visits: current.visits + 1, lastSeenAt: Date.now() });
}

export function recordOrbitVisit(orbitId: string) {
  const current = loadMayaProfile();
  const orbitVisits = { ...current.orbitVisits, [orbitId]: (current.orbitVisits[orbitId] ?? 0) + 1 };
  saveMayaProfile({ ...current, orbitVisits, lastOrbit: orbitId, lastSeenAt: Date.now() });
}

export function recordWhisper(id: string) {
  updateMayaProfile({ lastWhisperShown: id });
}
